import { Link } from 'react-router-dom';
import { Award, Bike, Crown, Settings, Users } from 'lucide-react';
import { getErrorMessage } from '@/lib/api';
import { cn, formatCOP } from '@/lib/cn';
import { useClasificacion } from '@/lib/queries';
import { Cargando, ErrorCarga, Vacio } from '@/components/ui';

/**
 * Los rankings del mes: quién más pide y quién más entrega.
 *
 * Dos columnas y los umbrales arriba, porque la pregunta que responde esta
 * pantalla es doble: quién ya se ganó su premio, y quién está tan cerca que
 * vale la pena empujarlo — un cliente a un pedido del premium es una
 * notificación bien puesta, no una casualidad.
 */
export function Clasificacion() {
  const clasificacion = useClasificacion();

  if (clasificacion.isLoading) return <Cargando texto="Cargando la clasificación…" />;
  if (clasificacion.isError) {
    return (
      <ErrorCarga
        mensaje={getErrorMessage(clasificacion.error)}
        onReintentar={() => void clasificacion.refetch()}
      />
    );
  }

  const { thresholds: u, clientes, mensajeros } = clasificacion.data!;

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl text-kick-dark">Clasificación del mes</h1>
          <p className="mt-1 text-sm text-kick-gray">
            El cliente que más pide gana la insignia premium; el mensajero que más entrega, su
            bono de recarga.
          </p>
        </div>
        <Link
          to="/ajustes"
          className="flex items-center gap-1.5 rounded-kick border border-kick-gray/25 px-3 py-1.5 text-sm font-semibold text-kick-ink transition hover:border-kick-gray/50"
        >
          <Settings className="h-4 w-4" />
          Cambiar umbrales
        </Link>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <div className="rounded-kick bg-white p-4 shadow-card">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-kick-gray">
            <Crown className="h-4 w-4 text-amber-500" />
            Premium
          </p>
          <p className="mt-1 text-sm text-kick-ink">
            {u.premiumOrdersPerMonth} pedidos al mes → {u.premiumDiscountPercent}% de descuento
            automático
          </p>
        </div>
        <div className="rounded-kick bg-white p-4 shadow-card">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-kick-gray">
            <Award className="h-4 w-4 text-emerald-600" />
            Bono del mensajero
          </p>
          <p className="mt-1 text-sm text-kick-ink">
            {u.courierBonusDeliveries} entregas en el período → {formatCOP(u.courierBonusAmount)}{' '}
            de recarga en su liquidación
          </p>
        </div>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <section className="card-kick p-5">
          <h2 className="flex items-center gap-2 text-lg text-kick-dark">
            <Users className="h-4 w-4 text-kick-gray" />
            Clientes que más piden
          </h2>

          {clientes.length === 0 ? (
            <div className="mt-4">
              <Vacio
                icono={<Users className="h-6 w-6" />}
                titulo="Sin entregas este mes"
                descripcion="Cuando haya pedidos entregados, el ranking aparece aquí."
              />
            </div>
          ) : (
            <ol className="mt-3 divide-y divide-kick-gray/10">
              {clientes.map((c, i) => (
                <li key={c.userId} className="flex items-center gap-3 py-2.5">
                  <Puesto lugar={i + 1} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate font-semibold text-kick-ink">{c.fullName}</span>
                      {c.premium && (
                        <span className="flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800">
                          <Crown className="h-3 w-3" />
                          Premium
                        </span>
                      )}
                    </span>
                    <span className="block text-xs text-kick-gray">@{c.username}</span>
                  </span>
                  <span className="text-right">
                    <span className="block font-semibold text-kick-ink">
                      {c.ordersCount} pedidos
                    </span>
                    <span className="block text-xs text-kick-gray">{formatCOP(c.spent)}</span>
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className="card-kick p-5">
          <h2 className="flex items-center gap-2 text-lg text-kick-dark">
            <Bike className="h-4 w-4 text-kick-gray" />
            Mensajeros que más entregan
          </h2>

          {mensajeros.length === 0 ? (
            <div className="mt-4">
              <Vacio
                icono={<Bike className="h-6 w-6" />}
                titulo="Sin entregas este mes"
                descripcion="Cuando haya entregas, el ranking aparece aquí."
              />
            </div>
          ) : (
            <ol className="mt-3 divide-y divide-kick-gray/10">
              {mensajeros.map((m, i) => (
                <li key={m.courierId} className="flex items-center gap-3 py-2.5">
                  <Puesto lugar={i + 1} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate font-semibold text-kick-ink">{m.fullName}</span>
                      {m.bono && (
                        <span className="flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-800">
                          <Award className="h-3 w-3" />
                          Bono
                        </span>
                      )}
                    </span>
                  </span>
                  <span className="text-right">
                    <span className="block font-semibold text-kick-ink">
                      {m.deliveriesCount} entregas
                    </span>
                    <span className="block text-xs text-kick-gray">ganó {formatCOP(m.earned)}</span>
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </div>
  );
}

/** El podio se ve; del cuarto para abajo basta el número. */
function Puesto({ lugar }: { lugar: number }) {
  return (
    <span
      className={cn(
        'grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-bold',
        lugar === 1
          ? 'bg-amber-100 text-amber-800'
          : lugar === 2
            ? 'bg-slate-200 text-slate-700'
            : lugar === 3
              ? 'bg-orange-100 text-orange-800'
              : 'bg-kick-light text-kick-gray',
      )}
    >
      {lugar}
    </span>
  );
}
